import { useCMSPageField } from '@/cms/fields'
import { useIntl } from '@/intl'
import { useAnalytics } from '@/scopes/common/infrastructure/analytics/hooks'
import { createPDPClickEvent } from '@/scopes/products/infrastructure/analytics'
import { getHeaderReview } from '@/scopes/products/pages/components/HeroSection/Reviews/ReviewsHeaderData'
import { useUserReviewsInitialData } from '@/scopes/products/user-reviews/hooks'
import { mqMin, space, tok, tokPx } from '@sunwebgroup/ui-components'
import { focusOutline, IconCell, IndeterminateProgressCircle, Sheet, Typography } from '@sunwebgroup/ui-components/uds'
import { isEmpty } from 'ramda'
import React, { useState } from 'react'
import { styled } from 'styled-components'
import { EXTRA_L_MIN_BREAKPOINT } from '../utils'
import { ExtendedContent } from './Result/ExtendedContent'
import ReviewerInfo from './ReviewerInfo'
import ReviewsSummary from './ReviewsSummary'
import { getFlagCode } from './utils'

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  border-radius: ${tokPx('primitives_corner_radius_l')};
  border: ${tokPx('semantics_dividers_tickness')} solid ${tok('semantics_boxes_border_color')};
  background: ${tok('semantics_views_background_color')};
  position: relative;
`
const OverlayButton = styled.button`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  border-radius: ${tokPx('primitives_corner_radius_l')};
  z-index: 1;

  ${focusOutline()}
`
const Header = styled.div`
  padding: ${space('primitives_space_16')};
  border-bottom: ${tokPx('semantics_dividers_tickness')} solid ${tok('semantics_boxes_border_color')};
  width: 100%;
  display: flex;
`
const Content = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
  width: 100%;
`
const Body = styled.div`
  padding: ${space('primitives_space_16')} ${space('primitives_space_16')} 0 ${space('primitives_space_16')};
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_12')};
`
const Footer = styled.div`
  padding: ${space('primitives_space_16')};
`
const FooterContent = styled(Typography)`
  text-decoration: underline;
`
const Review = styled(Typography)`
  overflow: hidden;
  position: relative;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;

  @media (min-width: ${EXTRA_L_MIN_BREAKPOINT}) {
    -webkit-line-clamp: 3;
  }
`
const UspsWrapper = styled.ol`
  margin: 0 0 ${space('primitives_space_16')} 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: ${space('primitives_space_4')};
`
const Usp = styled.li`
  display: flex;
  align-items: center;
  gap: ${space('primitives_space_8')};

    ${IconCell} {
        flex-shrink: 0;
    }
`
const IndeterminateProgressCircleWrapper = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`
const MinHeightWrapper = styled.div`
  min-height: 100px;

  @media ${mqMin('primitives_breakpoint_l_min')} {
    min-height: 180px;
  }
`
const ReviewerInfoWrapper = styled.div``

const SheetContent = styled(Sheet.Content)`
  height: 100%;
  @media ${mqMin('primitives_breakpoint_l_min')} {
      overflow: hidden;
    }
`

export default function Reviews({ isHeroSection = false }: { isHeroSection?: boolean }) {
  const intl = useIntl()
  const usps = useCMSPageField('PDP.usps')
  const { reviewsAmount, isLoading } = useUserReviewsInitialData()
  const ratingsCountDataSource = useCMSPageField('userReviews.ratingsCount')
  const shouldShowReviews = reviewsAmount > 2
  const shouldShowSummary = reviewsAmount > 2 && ratingsCountDataSource > 2
  const { sendEvent } = useAnalytics()
  const [showExtended, setShowExtended] = useState(false)

  const handleModalClose = () => {
    const nextOpen = !showExtended

    sendEvent(
      createPDPClickEvent({
        element: 'Bottom sheet',
        description: `${isHeroSection ? 'Top - ' : ''}${nextOpen ? 'Show more' : 'Hide'} information about the reviews`,
        result: `Modal with all reviews ${nextOpen ? 'opened' : 'closed'}`,
        type: 'Reviews extended modal',
      }),
    )

    setShowExtended(nextOpen)
  }

  // Temporary implementation for AB testing
  // Will be replaced with real mapped data when available
  const [review] = React.useState(() => getHeaderReview())

  if (isLoading) {
    return (
      <Wrapper>
        <MinHeightWrapper>
          <IndeterminateProgressCircleWrapper>
            <IndeterminateProgressCircle />
          </IndeterminateProgressCircleWrapper>
        </MinHeightWrapper>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      {shouldShowReviews && (
        <Sheet.Root open={showExtended} onOpenChange={handleModalClose}>
          <Sheet.Trigger>
            <OverlayButton />
          </Sheet.Trigger>
          <Sheet.Body side="bottom" hasPadding={false}>
            <Sheet.Header
              closeLabel={intl.formatMessage({ id: 'close' })}
              titleLabel={intl.formatMessage({ id: 'reviews_extended_heading' })}
            />
            <SheetContent>
              <ExtendedContent />
            </SheetContent>
          </Sheet.Body>
        </Sheet.Root>
      )}
      {shouldShowSummary
      && (
        <Header>
          <ReviewsSummary isHeroSection reviewsAmount={reviewsAmount} />
        </Header>
      )}
      <Content>
        <Body>
          {shouldShowReviews
            ? (
                <ReviewerInfoWrapper>
                  <ReviewerInfo
                    reviewerName={review.name}
                    flagCode={getFlagCode('NL')}
                  />
                </ReviewerInfoWrapper>
              )
            : <Typography $variant="display-h-regular">{intl.formatMessage({ id: 'hero_section_reasons_to_book_label' })}</Typography>}
          <Review>
            {shouldShowReviews
              ? <Typography>{review.content}</Typography>
              : (
                  !isEmpty(usps)
                  && (
                    <UspsWrapper>
                      {usps.slice(0, 4).map((item, index) => (
                        // eslint-disable-next-line react/no-array-index-key -- content is static and won't change
                        <Usp key={index}>
                          <IconCell glyph="CheckMarkSmall" />
                          <Typography>{item}</Typography>
                        </Usp>
                      ))}
                    </UspsWrapper>
                  )
                )}
          </Review>
        </Body>
        {shouldShowReviews && (
          <Footer>
            <FooterContent $variant="body-m-regular-tight">{intl.formatMessage({ id: 'reviews_view_all' })}</FooterContent>
          </Footer>
        )}
      </Content>
    </Wrapper>
  )
}
